/* ---------------------------------------------------------------------------
 * diamond-detail.js
 *
 * Vanilla module for the diamond detail page (sections/diamond-detail.liquid,
 * templates/page.diamond.json). Reads `id` from window.location.search,
 * fetches `/apps/diamonds/diamond?id=<id>` (or whatever override is set on
 * the `data-api-endpoint` attribute), and populates the server-rendered
 * skeleton.
 *
 * IMPORTANT constraints (see task fn-1-*.6 spec):
 *  - No frameworks, vanilla only (mirrors assets/diamond-search.js style).
 *  - MUST NOT mutate history — the back button has to restore the search
 *    grid AND its filter state, which lives in URL params on the search
 *    page. No pushState / replaceState in this file.
 *  - Loupe360 iframe is lazy-mounted (constructed client-side only after
 *    the diamond data confirms there IS a video URL). Never server-render
 *    the iframe; mobile Safari crashes if you do.
 *  - iframe attrs: sandbox, allow=fullscreen, referrerpolicy, loading=lazy.
 *  - Static image fallback when video missing; shape SVG fallback when
 *    neither video nor image exist.
 * ------------------------------------------------------------------------- */

const DEFAULT_ENDPOINT = '/apps/diamonds/diamond';

/** Grab the id query param without touching history. */
function readIdFromLocation() {
  try {
    const sp = new URLSearchParams(window.location.search);
    const raw = sp.get('id');
    if (!raw) return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    return trimmed;
  } catch (_) {
    return null;
  }
}

/** Very permissive id regex — mirrors vercel/api/diamond.ts isValidDiamondId. */
const ID_RE = /^[A-Za-z0-9_\-]+$/;

function isValidId(id) {
  return typeof id === 'string' && id.length > 0 && id.length <= 128 && ID_RE.test(id);
}

/** USD formatter — no cents, thousands separators. */
const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** Format a dash placeholder for any missing/blank/"-" value. */
function fmt(v) {
  if (v == null) return '—';
  const s = String(v).trim();
  if (s.length === 0 || s === '-') return '—';
  return s;
}

/** Title-case a single word (shape labels). */
function titleCase(s) {
  if (!s) return '';
  return String(s)
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Compute L/W ratio from a "L x W x H mm" measurements string. */
function computeLwRatio(measurements) {
  if (typeof measurements !== 'string') return '—';
  const m = measurements.match(/([\d.]+)\s*x\s*([\d.]+)/);
  if (!m) return '—';
  const l = Number(m[1]);
  const w = Number(m[2]);
  if (!Number.isFinite(l) || !Number.isFinite(w) || w <= 0) return '—';
  return (l / w).toFixed(2);
}

/**
 * Build the `X.YZct Shape Lab Grown Diamond` title. Mirrors
 * `toLineItemTitle` in vercel/lib/diamond-row.ts but without the lab/report
 * suffix — the title is already long enough without it.
 */
function buildTitle(row) {
  const carats = Number(row.carats);
  const caratsStr = Number.isFinite(carats) ? carats.toFixed(2) : String(row.carats || '');
  const shape = row.shape ? titleCase(row.shape) : '';
  return `${caratsStr}ct ${shape} Lab Grown Diamond`.trim();
}

/**
 * Mount the media column. Lazy — builds the iframe/img element in JS so
 * mobile Safari does not try to fetch the Loupe360 bundle before we know
 * the diamond has a video.
 */
function mountMedia(mediaEl, row) {
  if (!mediaEl) return;
  // Clear any prior contents (e.g. if JS is re-run on navigation).
  while (mediaEl.firstChild) mediaEl.removeChild(mediaEl.firstChild);

  if (row.video) {
    const iframe = document.createElement('iframe');
    iframe.src = row.video;
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin');
    iframe.setAttribute('allow', 'fullscreen');
    iframe.setAttribute('referrerpolicy', 'no-referrer-when-downgrade');
    iframe.setAttribute('loading', 'lazy');
    iframe.setAttribute('title', 'Diamond 360° viewer');
    mediaEl.appendChild(iframe);
    return;
  }

  if (row.image) {
    const img = document.createElement('img');
    img.src = row.image;
    img.alt = buildTitle(row);
    img.loading = 'lazy';
    img.decoding = 'async';
    mediaEl.appendChild(img);
    return;
  }

  // Fallback: reuse the same inline SVG used on the search card. We don't
  // have Liquid at runtime, so render a minimal diamond silhouette; the
  // card snippet's SVGs are richer but this is a graceful degradation.
  const svgNs = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(svgNs, 'svg');
  svg.setAttribute('class', 'diamond-shape-icon');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svgNs, 'path');
  path.setAttribute('d', 'M16 4 L28 14 L16 28 L4 14 Z');
  svg.appendChild(path);
  mediaEl.appendChild(svg);
}

/** Set the text of a spec `<dd data-spec="key">`. */
function setSpec(rootEl, key, value) {
  const el = rootEl.querySelector(`[data-spec="${key}"]`);
  if (el) el.textContent = value;
}

/** Populate the entire info column from a row object. */
function renderRow(rootEl, row) {
  const titleEl = rootEl.querySelector('[data-diamond-title]');
  if (titleEl) titleEl.textContent = buildTitle(row);

  const priceEl = rootEl.querySelector('[data-diamond-price]');
  if (priceEl) {
    const price = Number(row.price);
    priceEl.textContent = Number.isFinite(price) ? USD.format(price) : '';
  }

  // Affirm teaser. Only shown when price >= configured threshold.
  const affirmEl = rootEl.querySelector('[data-diamond-affirm]');
  if (affirmEl) {
    const price = Number(row.price);
    const threshold = Number(rootEl.getAttribute('data-affirm-min-price') || '50');
    const months = Number(rootEl.getAttribute('data-affirm-months') || '12');
    if (Number.isFinite(price) && price >= threshold && months > 0) {
      const perMonth = Math.round(price / months);
      affirmEl.textContent = `From ${USD.format(perMonth)}/mo with Affirm`;
      affirmEl.hidden = false;
    } else {
      affirmEl.hidden = true;
    }
  }

  // Specs. `cut === '-'` renders as `—` per spec.
  setSpec(rootEl, 'shape', row.shape ? titleCase(row.shape) : '—');
  const carats = Number(row.carats);
  setSpec(rootEl, 'carats', Number.isFinite(carats) ? `${carats.toFixed(2)} ct` : '—');
  setSpec(rootEl, 'color', fmt(row.color));
  setSpec(rootEl, 'clarity', fmt(row.clarity));
  setSpec(rootEl, 'cut', fmt(row.cut));
  setSpec(rootEl, 'polish', fmt(row.polish));
  setSpec(rootEl, 'symmetry', fmt(row.symmetry));
  setSpec(rootEl, 'fluorescence', fmt(row.fluorescence));
  setSpec(rootEl, 'measurements', row.measurements || '—');
  setSpec(rootEl, 'lw_ratio', computeLwRatio(row.measurements));
  // Table/Depth % are not in the Diamond projection today — task-future
  // work: add them to lib/diamond-row.ts. Rendered as "—" for now so the
  // row doesn't look broken.
  setSpec(rootEl, 'table_pct', '—');
  setSpec(rootEl, 'depth_pct', '—');
  setSpec(rootEl, 'lab', fmt(row.lab));
  setSpec(rootEl, 'report_no', fmt(row.report_no));

  // Certificate link / fallback.
  const certLink = rootEl.querySelector('[data-diamond-cert-link]');
  const certFallback = rootEl.querySelector('[data-diamond-cert-fallback]');
  if (row.pdf) {
    if (certLink) {
      certLink.href = row.pdf;
      certLink.target = '_blank';
      certLink.rel = 'noopener noreferrer';
      const labLabel = row.lab ? String(row.lab) : '';
      certLink.textContent = labLabel
        ? `View certificate (${labLabel})`
        : 'View certificate';
      certLink.hidden = false;
    }
    if (certFallback) certFallback.hidden = true;
  } else {
    if (certLink) certLink.hidden = true;
    if (certFallback) certFallback.hidden = false;
  }

  // Media (lazy mount).
  mountMedia(rootEl.querySelector('[data-diamond-media]'), row);
}

/** Toggle the visibility of the loading / error / not-found / body blocks. */
function showState(rootEl, state) {
  const loading = rootEl.querySelector('[data-diamond-loading]');
  const error = rootEl.querySelector('[data-diamond-error]');
  const notfound = rootEl.querySelector('[data-diamond-notfound]');
  const body = rootEl.querySelector('[data-diamond-body]');
  if (loading) loading.hidden = state !== 'loading';
  if (error) error.hidden = state !== 'error';
  if (notfound) notfound.hidden = state !== 'notfound';
  if (body) body.hidden = state !== 'body';
}

/** Resolve the API endpoint from the host element's data attribute. */
function resolveEndpoint(rootEl) {
  const attr = rootEl && rootEl.getAttribute('data-api-endpoint');
  if (attr && attr.trim()) return attr.trim();
  return DEFAULT_ENDPOINT;
}

/**
 * Fetch a diamond by id. Returns:
 *   { row }            on 200
 *   { notFound: true } on 404
 *   throws Error       on any other error
 */
async function fetchDiamondById(endpoint, id, { signal } = {}) {
  const url = `${endpoint}?id=${encodeURIComponent(id)}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    signal,
  });
  if (response.status === 404) return { notFound: true };
  if (!response.ok) {
    throw new Error(`diamond fetch failed: ${response.status}`);
  }
  const payload = await response.json();
  if (!payload || !payload.row) {
    throw new Error('diamond fetch: malformed payload');
  }
  return { row: payload.row };
}

/** Custom element: handles load + state transitions. */
class DiamondDetail extends HTMLElement {
  constructor() {
    super();
    this._abort = null;
  }

  connectedCallback() {
    const retryBtn = this.querySelector('[data-diamond-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.load());
    }
    this.load();
  }

  disconnectedCallback() {
    if (this._abort) {
      this._abort.abort();
      this._abort = null;
    }
  }

  async load() {
    const id = readIdFromLocation();
    if (!isValidId(id)) {
      showState(this, 'notfound');
      return;
    }

    showState(this, 'loading');

    // Abort any in-flight fetch before starting a new one.
    if (this._abort) this._abort.abort();
    const ac = new AbortController();
    this._abort = ac;

    const endpoint = resolveEndpoint(this);
    try {
      const result = await fetchDiamondById(endpoint, id, { signal: ac.signal });
      if (ac.signal.aborted) return;
      if (result.notFound) {
        showState(this, 'notfound');
        return;
      }
      renderRow(this, result.row);
      // Expose the loaded diamond id to the <diamond-buy-button> so its click
      // handler can POST the correct id without re-parsing URLSearchParams.
      const buyBtn = this.querySelector('[data-diamond-buy-button]');
      if (buyBtn) buyBtn.setAttribute('data-diamond-id', result.row.id || id);
      // Same pattern for the Add to Cart button — diamond-cart.js reads the
      // id off `data-diamond-id` when the click fires.
      const addBtn = this.querySelector('[data-diamond-add-to-cart]');
      if (addBtn) addBtn.setAttribute('data-diamond-id', result.row.id || id);
      // Same pattern for the wishlist button — it needs the resolved id to
      // key into the localStorage store. See assets/wishlist.js.
      const wishBtn = this.querySelector('wishlist-button[data-diamond-wishlist]');
      if (wishBtn) {
        wishBtn.setAttribute('data-id', result.row.id || id);
        if (typeof wishBtn.render === 'function') wishBtn.render();
      }
      showState(this, 'body');
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      console.error('[diamond-detail] load failed', err);
      showState(this, 'error');
    }
  }
}

if (!customElements.get('diamond-detail')) {
  customElements.define('diamond-detail', DiamondDetail);
}

/* ---------------------------------------------------------------------------
 * <button is="diamond-buy-button">
 *
 * Customized built-in that owns the Buy Now flow:
 *   1. click → preventDefault, disable, swap text, dispatch theme:loading:start
 *   2. POST {id} to data-checkout-endpoint (default /apps/diamonds/checkout)
 *   3. 200 { invoiceUrl } → window.location.href = invoiceUrl
 *   4. error (network/4xx/5xx) → restore button, unhide inline error snippet
 *   5. 5s fallback timer: if the redirect never lands, re-enable + show error
 *
 * The id is resolved in this order:
 *   - data-diamond-id (set by <diamond-detail> after successful fetch)
 *   - URLSearchParams(window.location.search).get('id')
 *
 * theme:loading:start / theme:loading:end reuses the existing <loading-bar>
 * global from layout/theme.liquid.
 * ------------------------------------------------------------------------- */

const DEFAULT_CHECKOUT_ENDPOINT = '/apps/diamonds/checkout';
const REDIRECT_FALLBACK_MS = 5000;

/** Resolve the diamond id for a given buy button. */
function resolveDiamondId(btn) {
  const attr = btn.getAttribute('data-diamond-id');
  if (attr && attr.trim()) return attr.trim();
  return readIdFromLocation();
}

/** Resolve the checkout endpoint, falling back to the default app-proxy path. */
function resolveCheckoutEndpoint(btn) {
  const attr = btn.getAttribute('data-checkout-endpoint');
  if (attr && attr.trim()) return attr.trim();
  return DEFAULT_CHECKOUT_ENDPOINT;
}

/** Locate the sibling inline error snippet (rendered by the Liquid template). */
function findErrorEl(btn) {
  // The error snippet is always a sibling inside `.diamond-detail__cta`.
  const root = btn.closest('.diamond-detail__cta') || btn.parentElement;
  if (!root) return null;
  return root.querySelector('[data-diamond-checkout-error]');
}

class DiamondBuyButton extends HTMLButtonElement {
  constructor() {
    super();
    this._onClick = this._onClick.bind(this);
    this._inFlight = false;
    this._fallbackTimer = null;
  }

  connectedCallback() {
    // Cache the original text once so error recovery can restore it exactly
    // (including whitespace introduced by Liquid output).
    if (!this.hasAttribute('data-original-text')) {
      this.setAttribute('data-original-text', this.textContent.trim());
    }
    this.addEventListener('click', this._onClick);

    // Wire the retry button in the inline error snippet. The snippet is
    // rendered server-side but hidden until we unhide it.
    const errorEl = findErrorEl(this);
    if (errorEl) {
      const retryBtn = errorEl.querySelector('[data-diamond-checkout-retry]');
      if (retryBtn && !retryBtn._diamondWired) {
        retryBtn._diamondWired = true;
        retryBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this._hideError();
          this._submit();
        });
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this._clearFallback();
  }

  _onClick(event) {
    event.preventDefault();
    if (this._inFlight) return;
    this._hideError();
    this._submit();
  }

  _setBusy() {
    this._inFlight = true;
    this.setAttribute('aria-busy', 'true');
    this.disabled = true;
    // Locale key `diamond_detail.preparing` is added in en.default.json. We
    // don't have access to Liquid at runtime, so fall back to a literal.
    const preparing =
      this.getAttribute('data-preparing-text') || 'Preparing checkout…';
    this.textContent = preparing;
  }

  _restore() {
    this._inFlight = false;
    this.removeAttribute('aria-busy');
    this.disabled = false;
    const orig = this.getAttribute('data-original-text');
    if (orig != null) this.textContent = orig;
    this._clearFallback();
  }

  _clearFallback() {
    if (this._fallbackTimer != null) {
      clearTimeout(this._fallbackTimer);
      this._fallbackTimer = null;
    }
  }

  _showError() {
    const errorEl = findErrorEl(this);
    if (errorEl) errorEl.hidden = false;
  }

  _hideError() {
    const errorEl = findErrorEl(this);
    if (errorEl) errorEl.hidden = true;
  }

  async _submit() {
    const id = resolveDiamondId(this);
    if (!isValidId(id)) {
      // No id at all — surface the error state. This is a defensive guard;
      // the button shouldn't normally be rendered before the diamond loads.
      this._showError();
      return;
    }

    this._setBusy();
    // Reuse the existing <loading-bar> global (layout/theme.liquid).
    document.dispatchEvent(
      new CustomEvent('theme:loading:start', { bubbles: true }),
    );

    const endpoint = resolveCheckoutEndpoint(this);
    let response;
    try {
      response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id }),
      });
    } catch (err) {
      console.error('[diamond-buy-button] network error', err);
      document.dispatchEvent(
        new CustomEvent('theme:loading:end', { bubbles: true }),
      );
      this._restore();
      this._showError();
      return;
    }

    document.dispatchEvent(
      new CustomEvent('theme:loading:end', { bubbles: true }),
    );

    if (!response.ok) {
      console.error('[diamond-buy-button] checkout failed', response.status);
      this._restore();
      this._showError();
      return;
    }

    let payload;
    try {
      payload = await response.json();
    } catch (err) {
      console.error('[diamond-buy-button] malformed checkout payload', err);
      this._restore();
      this._showError();
      return;
    }

    const invoiceUrl = payload && payload.invoiceUrl;
    if (typeof invoiceUrl !== 'string' || invoiceUrl.length === 0) {
      console.error('[diamond-buy-button] missing invoiceUrl in response');
      this._restore();
      this._showError();
      return;
    }

    // 5-second fallback: if for any reason the redirect never lands (extension
    // interception, navigation blocked, etc.), re-enable the button and show
    // the error state so the user isn't stuck staring at a disabled button.
    this._fallbackTimer = setTimeout(() => {
      console.warn('[diamond-buy-button] redirect fallback fired');
      this._restore();
      this._showError();
    }, REDIRECT_FALLBACK_MS);

    // Do the redirect. Keep the button in its disabled/busy state so users
    // can't double-click while the browser tears down the page.
    window.location.href = invoiceUrl;
  }
}

if (!customElements.get('diamond-buy-button')) {
  customElements.define('diamond-buy-button', DiamondBuyButton, {
    extends: 'button',
  });
}
